
import React from "react";
import { Link, useParams } from "react-router-dom";

import {
  useGetCoachingCenterInvoiceByIdQuery,
} from "../../slices/billingApiSlice";

const CoachingCenterInvoiceDetails = () => {
  const { invoiceId } = useParams();

  // ======================================================
  // Coaching Center Invoice Details
  // GET /api/billing/coaching-center/invoices/:invoiceId
  // ======================================================

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetCoachingCenterInvoiceByIdQuery(invoiceId, {
    skip: !invoiceId,
  });

  const invoice = data?.data;

  // ======================================================
  // Helpers
  // ======================================================

  const formatCurrency = (amount = 0) => {
    const numericAmount = Number(amount || 0);

    return `₹${numericAmount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatStatus = (status) => {
    if (!status) return "-";

    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatDiscountType = (type) => {
    if (!type) return "-";

    if (type === "percentage") return "Percentage";

    if (type === "fixed") return "Fixed Amount";

    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatPaymentMethod = (method) => {
    if (!method) return "-";

    return method
      .split("_")
      .map(
        (word) => word.charAt(0).toUpperCase() + word.slice(1),
      )
      .join(" ");
  };

  const getStatusClasses = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-700";

      case "cancelled":
        return "bg-red-100 text-red-700";

      case "overdue":
        return "bg-red-100 text-red-700";

      default:
        return "bg-yellow-100 text-yellow-700";
    }
  };

  // ======================================================
  // Loading
  // ======================================================

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-gray-600">
          Loading invoice details...
        </div>
      </div>
    );
  }

  // ======================================================
  // Error
  // ======================================================

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="rounded-xl border bg-white p-8 text-center shadow-sm">
          <h2 className="text-xl font-semibold text-red-600">
            Unable to load invoice
          </h2>

          <p className="mt-2 text-sm text-red-600">
            {error?.data?.message ||
              error?.error ||
              "The invoice could not be found or you do not have permission to view it."}
          </p>

          <div className="mt-5 flex justify-center gap-3">
            <button
              type="button"
              onClick={refetch}
              disabled={isFetching}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {isFetching ? "Trying..." : "Try Again"}
            </button>

            <Link
              to="/billing/coaching-center/invoices"
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Back to Invoices
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ======================================================
  // Invoice Not Found
  // ======================================================

  if (!invoice) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="rounded-xl border bg-white p-8 text-center shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900">
            Invoice not found
          </h2>

          <p className="mt-2 text-sm text-gray-500">
            The requested invoice does not exist or does not belong to your
            coaching center.
          </p>

          <Link
            to="/billing/coaching-center/invoices"
            className="mt-5 inline-block rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Back to Invoices
          </Link>
        </div>
      </div>
    );
  }

  // ======================================================
  // IMPORTANT:
  // Use the same fields as InvoiceDetailsScreen.
  // ======================================================

  // Subtotal
  const subtotal = Number(
    invoice.calculatedSubtotal ?? invoice.subtotal ?? 0,
  );

  // Applied discount
  const discountAmount = Number(
    invoice.discountAmount ?? 0,
  );

  // Taxable amount
  const taxableAmount = Number(
    invoice.taxableAmount ?? 0,
  );

  // GST
  const totalTaxAmount = Number(
    invoice.totalTaxAmount ?? 0,
  );

  const gstRate = Number(
    invoice.gstRate ?? 0,
  );

  // GST type
  const isCgstSgst = invoice.gstType === "cgst_sgst";
  const isIgst = invoice.gstType === "igst";

  // Individual GST components
  const cgstRate = Number(invoice.cgstRate ?? 0);
  const cgstAmount = Number(invoice.cgstAmount ?? 0);

  const sgstRate = Number(invoice.sgstRate ?? 0);
  const sgstAmount = Number(invoice.sgstAmount ?? 0);

  const igstRate = Number(invoice.igstRate ?? 0);
  const igstAmount = Number(invoice.igstAmount ?? 0);

  // Total
  const total = Number(
    invoice.total ?? 0,
  );

  // Payment
  const paidAmount = Number(
    invoice.paidAmount ?? 0,
  );

  const balance = Math.max(
    total - paidAmount,
    0,
  );

  // ======================================================
  // Discount Settings
  // ======================================================

  /*
   * IMPORTANT:
   * The working admin invoice screen uses discountSettings.
   *
   * Preferred:
   * invoice.discountSettings
   *
   * Also support the other locations for backward compatibility.
   */

  const discountSettings =
    invoice.discountSettings ||
    invoice.billingSettings?.discount ||
    invoice.discount ||
    null;

  const discountEnabled =
    discountSettings?.enabled === true ||
    discountAmount > 0;

  const discountType =
    discountSettings?.type;

  const discountConfiguredValue = Number(
    discountSettings?.value ?? 0,
  );

  const discountMaxAmount =
    discountSettings?.maxAmount !== null &&
    discountSettings?.maxAmount !== undefined
      ? Number(discountSettings.maxAmount)
      : null;

  const discountReason =
    discountSettings?.reason;

  const discountValidFrom =
    discountSettings?.validFrom;

  const discountValidUntil =
    discountSettings?.validUntil;

  // ======================================================
  // Render
  // ======================================================

  return (
    <div className="space-y-6">

      {/* ==================================================
          Header
      ================================================== */}

      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <div className="mb-2">
            <Link
              to="/billing/coaching-center/invoices"
              className="text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              ← Back to Invoices
            </Link>
          </div>

          <h1 className="text-2xl font-bold text-gray-900">
            Invoice Details
          </h1>

          <p className="mt-1 text-sm text-gray-500">
            Invoice #{invoice.invoiceNumber || invoice._id}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span
            className={`rounded-full px-4 py-2 text-sm font-semibold capitalize ${getStatusClasses(
              invoice.status,
            )}`}
          >
            {invoice.status || "pending"}
          </span>

          <button
            type="button"
            onClick={refetch}
            disabled={isFetching}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {isFetching ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* ==================================================
          Invoice + Coaching Center Information
      ================================================== */}

      <div className="grid gap-6 lg:grid-cols-2">

        {/* Invoice Information */}

        <div className="rounded-xl border bg-white">
          <div className="border-b px-6 py-4">
            <h2 className="font-semibold text-gray-900">
              Invoice Information
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-5 p-6 sm:grid-cols-2">

            <InfoItem
              label="Invoice Number"
              value={invoice.invoiceNumber}
            />

            <InfoItem
              label="Invoice Date"
              value={formatDate(invoice.invoiceDate)}
            />

            <InfoItem
              label="Due Date"
              value={formatDate(invoice.dueDate)}
            />

            <InfoItem
              label="Billing Period"
              value={
                invoice.billingPeriodStart ||
                invoice.billingPeriodEnd
                  ? `${formatDate(
                      invoice.billingPeriodStart,
                    )} - ${formatDate(
                      invoice.billingPeriodEnd,
                    )}`
                  : "-"
              }
            />

            <InfoItem
              label="Created At"
              value={formatDateTime(invoice.createdAt)}
            />

            <InfoItem
              label="Status"
              value={formatStatus(invoice.status)}
            />

          </div>
        </div>

        {/* Coaching Center */}

        <div className="rounded-xl border bg-white">
          <div className="border-b px-6 py-4">
            <h2 className="font-semibold text-gray-900">
              Coaching Center
            </h2>
          </div>

          <div className="space-y-5 p-6">

            <InfoItem
              label="Name"
              value={
                invoice.coachingCenter?.name ||
                invoice.coachingCenterName
              }
            />

            <InfoItem
              label="Phone"
              value={
                invoice.coachingCenter?.phone ||
                invoice.phone
              }
            />

            <InfoItem
              label="Email"
              value={
                invoice.coachingCenter?.email ||
                invoice.email
              }
            />

            <InfoItem
              label="Subscription"
              value={invoice.subscription?.planName}
            />

            <InfoItem
              label="Subscription Status"
              value={invoice.subscription?.status}
            />

          </div>
        </div>

      </div>

      {/* ==================================================
          Usage Snapshot
      ================================================== */}

      <div className="rounded-xl border bg-white">

        <div className="border-b px-6 py-4">
          <h2 className="font-semibold text-gray-900">
            Usage Snapshot
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-5 p-6 sm:grid-cols-2">

          <Metric
            label="Center Admins"
            value={invoice.adminCount}
          />

          <Metric
            label="Active Students"
            value={invoice.activeStudentCount}
          />

        </div>
      </div>

      {/* ==================================================
          Billing Items
      ================================================== */}

      <div className="rounded-xl border bg-white">

        <div className="border-b px-6 py-4">
          <h2 className="font-semibold text-gray-900">
            Billing Items
          </h2>
        </div>

        <div className="overflow-x-auto p-6">

          <table className="min-w-full border border-collapse">

            <thead className="bg-gray-100">
              <tr>
                <th className="border p-3 text-left">
                  Description
                </th>

                <th className="border p-3 text-center">
                  Qty
                </th>

                <th className="border p-3 text-right">
                  Unit Price
                </th>

                <th className="border p-3 text-right">
                  Amount
                </th>
              </tr>
            </thead>

            <tbody>

              {invoice.lineItems?.length > 0 ? (
                invoice.lineItems.map((item, index) => (
                  <tr key={item._id || index}>

                    <td className="border p-3">
                      {item.description}
                    </td>

                    <td className="border p-3 text-center">
                      {item.quantity}
                    </td>

                    <td className="border p-3 text-right">
                      {formatCurrency(item.unitPrice)}
                    </td>

                    <td className="border p-3 text-right">
                      {formatCurrency(item.amount)}
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    className="border p-5 text-center text-gray-500"
                  >
                    No billing items found.
                  </td>
                </tr>
              )}

            </tbody>
          </table>

        </div>
      </div>

      {/* ==================================================
          Center Billing Settings / Discount
      ================================================== */}

      <div className="rounded-xl border bg-white">

        <div className="border-b px-6 py-4">

          <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">

            <div>
              <h2 className="font-semibold text-gray-900">
                Discount Configuration
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Coaching-center level billing setting
              </p>
            </div>

            <span
              className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold ${
                discountEnabled
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {discountEnabled
                ? "Discount Enabled"
                : "No Discount"}
            </span>

          </div>

        </div>

        <div className="p-6">

          {discountEnabled ? (

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">

              <InfoItem
                label="Discount Type"
                value={formatDiscountType(discountType)}
              />

              <InfoItem
                label="Configured Value"
                value={
                  discountType === "percentage"
                    ? `${discountConfiguredValue}%`
                    : formatCurrency(
                        discountConfiguredValue,
                      )
                }
              />

              {discountType === "percentage" && (
                <InfoItem
                  label="Maximum Discount"
                  value={
                    discountMaxAmount !== null
                      ? formatCurrency(discountMaxAmount)
                      : "No maximum"
                  }
                />
              )}

              <InfoItem
                label="Applied Discount"
                value={formatCurrency(discountAmount)}
                valueClassName="text-green-600"
              />

              <InfoItem
                label="Valid From"
                value={formatDate(discountValidFrom)}
              />

              <InfoItem
                label="Valid Until"
                value={formatDate(discountValidUntil)}
              />

              {discountReason && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <p className="font-semibold text-gray-900">
                    Reason
                  </p>

                  <p className="mt-1 text-gray-600">
                    {discountReason}
                  </p>
                </div>
              )}

            </div>

          ) : (

            <div className="text-sm text-gray-500">
              No center-level discount was applied to this invoice.
            </div>

          )}

        </div>
      </div>

      {/* ==================================================
          Amount Summary
      ================================================== */}

      <div className="rounded-xl border bg-white">

        <div className="border-b px-6 py-4">
          <h2 className="font-semibold text-gray-900">
            Amount Summary
          </h2>
        </div>

        <div className="space-y-4 p-6">

          <SummaryRow
            label="Calculated Subtotal"
            value={formatCurrency(subtotal)}
          />

          <SummaryRow
            label="Center Discount"
            value={formatCurrency(discountAmount)}
            valueClassName={
              discountAmount > 0
                ? "text-green-600"
                : ""
            }
          />

          {discountEnabled && discountAmount > 0 && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">

              <div className="flex justify-between gap-4">

                <span>
                  {discountType === "percentage"
                    ? `Discount (${discountConfiguredValue}%)`
                    : "Fixed Discount"}
                </span>

                <strong>
                  - {formatCurrency(discountAmount)}
                </strong>

              </div>

              {discountReason && (
                <p className="mt-1 text-xs text-green-600">
                  {discountReason}
                </p>
              )}

            </div>
          )}

          {invoice.minimumInvoiceApplied && (
            <div className="flex items-center justify-between gap-4 rounded-lg bg-orange-50 px-3 py-2 text-sm text-orange-600">

              <span>
                Minimum Invoice Applied
              </span>

              <span className="font-semibold">
                {formatCurrency(taxableAmount)}
              </span>

            </div>
          )}

          <div className="border-t pt-4">

            <SummaryRow
              label="Taxable Amount"
              value={formatCurrency(taxableAmount)}
              bold
            />

          </div>

        </div>
      </div>

      {/* ==================================================
          GST Summary
      ================================================== */}

      <div className="rounded-xl border bg-white">

        <div className="border-b px-6 py-4">
          <h2 className="font-semibold text-gray-900">
            GST Summary
          </h2>
        </div>

        <div className="space-y-4 p-6">

          {/* Total GST */}

          <SummaryRow
            label={`GST (${gstRate}%)`}
            value={formatCurrency(totalTaxAmount)}
            bold
          />

          {/* CGST + SGST */}

          {isCgstSgst && (
            <>
              <SummaryRow
                label={`CGST (${cgstRate}%)`}
                value={formatCurrency(cgstAmount)}
              />

              <SummaryRow
                label={`SGST (${sgstRate}%)`}
                value={formatCurrency(sgstAmount)}
              />
            </>
          )}

          {/* IGST */}

          {isIgst && (
            <SummaryRow
              label={`IGST (${igstRate}%)`}
              value={formatCurrency(igstAmount)}
            />
          )}

          {/* No GST */}

          {invoice.gstType === "none" && (
            <p className="text-sm text-gray-500">
              No GST applicable.
            </p>
          )}

          <div className="border-t pt-4">

            <SummaryRow
              label="Total Amount"
              value={formatCurrency(total)}
              bold
              large
            />

          </div>

        </div>
      </div>

      {/* ==================================================
          Payment Information
      ================================================== */}

      <div className="rounded-xl border bg-white">

        <div className="border-b px-6 py-4">
          <h2 className="font-semibold text-gray-900">
            Payment Information
          </h2>
        </div>

        <div className="grid gap-6 p-6 sm:grid-cols-2 lg:grid-cols-4">

          <InfoItem
            label="Invoice Total"
            value={formatCurrency(total)}
          />

          <InfoItem
            label="Paid Amount"
            value={formatCurrency(paidAmount)}
            valueClassName="text-green-600"
          />

          <InfoItem
            label="Balance"
            value={formatCurrency(balance)}
            valueClassName={
              balance > 0
                ? "text-red-600"
                : "text-green-600"
            }
          />

          <InfoItem
            label="Payment Status"
            value={formatStatus(invoice.status)}
          />

          <InfoItem
            label="Payment Method"
            value={formatPaymentMethod(
              invoice.paymentMethod,
            )}
          />

          <InfoItem
            label="Payment Reference"
            value={invoice.paymentReference}
          />

          <InfoItem
            label="Paid At"
            value={formatDateTime(invoice.paidAt)}
          />

        </div>
      </div>

      {/* ==================================================
          Cancellation
      ================================================== */}

      {invoice.status === "cancelled" && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6">

          <h2 className="font-semibold text-red-800">
            Cancellation
          </h2>

          <div className="mt-3 space-y-2 text-sm text-red-700">

            <p>
              <strong>Cancelled At:</strong>{" "}
              {formatDate(invoice.cancelledAt)}
            </p>

            <p>
              <strong>Reason:</strong>{" "}
              {invoice.cancellationReason ||
                "No reason provided"}
            </p>

          </div>
        </div>
      )}

      {/* ==================================================
          Technical Information
      ================================================== */}

      <div className="rounded-xl border bg-gray-50 p-4">

        <div className="text-xs text-gray-500">
          Invoice ID
        </div>

        <div className="mt-1 break-all font-mono text-sm text-gray-700">
          {invoice._id}
        </div>

      </div>

    </div>
  );
};

// ======================================================
// Info Item
// ======================================================

const InfoItem = ({
  label,
  value,
  valueClassName = "",
}) => {
  return (
    <div>
      <div className="text-sm font-semibold text-gray-700">
        {label}
      </div>

      <div
        className={`mt-1 text-sm font-medium text-gray-900 ${valueClassName}`}
      >
        {value ?? "-"}
      </div>
    </div>
  );
};

// ======================================================
// Metric
// ======================================================

const Metric = ({
  label,
  value,
}) => {
  return (
    <div>
      <div className="text-sm font-semibold text-gray-700">
        {label}
      </div>

      <div className="mt-1 text-2xl font-bold text-gray-800">
        {value ?? 0}
      </div>
    </div>
  );
};

// ======================================================
// Summary Row
// ======================================================

const SummaryRow = ({
  label,
  value,
  bold = false,
  large = false,
  valueClassName = "",
}) => {
  return (
    <div className="flex items-center justify-between gap-4">
      <span
        className={
          bold
            ? "font-semibold text-gray-900"
            : "text-sm text-gray-600"
        }
      >
        {label}
      </span>

      <span
        className={`
          whitespace-nowrap
          ${bold ? "font-bold" : "font-medium"}
          ${large ? "text-xl" : "text-sm"}
          text-gray-900
          ${valueClassName}
        `}
      >
        {value}
      </span>
    </div>
  );
};

export default CoachingCenterInvoiceDetails;

